import { Layout, Select } from "antd";

import { useMediaQuery } from "react-responsive";
import { PageList } from "../../config/pages";
import { NavLink, useLocation, useNavigate, Routes, Route } from "react-router-dom";
import { useEffect, useState } from "react";
import { withRouter } from "../../utils/router";
import { useLoginModal } from "../../utils/hooks";
import { AccountModal, configOpenAccountModal } from "../account";
import { getCookie } from "../../utils/common";
import { StockScoreProvider } from '../stock-score-list';
import BrandLogo from "../../components/BrandLogo";
import { MARKET_TABS, rememberMarketTab, resolveMarketKey } from "../../utils/marketTab";

// 检查cookie中是否存在stockservant_token这个key
const hasStockServantToken = getCookie('stockservant_token');


const { Header, Sider, Content } = Layout;

export const DesktopScreen = ({ children }) => {
  return useMediaQuery({ minWidth: 901 }) ? children : null;
};

export const MobileScreen = ({ children }) => {
  return useMediaQuery({ maxWidth: 900 }) ? children : null;
};

function Homepage() {
  const { isLoginModalOpen, setIsLoginModalOpen } = useLoginModal();
  const location = useLocation();
  const navigate = useNavigate();
  configOpenAccountModal(() => setIsLoginModalOpen(true));

  const isWelcomePage = location.pathname === "/";
  const [pendingMarket, setPendingMarket] = useState(null);
  const marketKey = isWelcomePage && pendingMarket
    ? pendingMarket
    : resolveMarketKey(location.pathname);

  useEffect(() => {
    rememberMarketTab(marketKey);
  }, [marketKey]);

  useEffect(() => {
    if (!isWelcomePage) {
      setPendingMarket(null);
    }
  }, [isWelcomePage]);

  const changeMarket = (value) => {
    const target = MARKET_TABS.find((item) => item.value === value);
    if (!target) {
      return;
    }
    rememberMarketTab(value);
    // 说明页上的下拉只更新待进入的 tab，由「我已知晓」进入
    if (isWelcomePage) {
      setPendingMarket(value);
      return;
    }
    navigate(target.path);
  };

  return (
    <div>
      {isLoginModalOpen && (
        <AccountModal
          onCancel={() => {
            setIsLoginModalOpen(false);
            navigate("/");
          }}
          onLoginSuc={() => {
            setIsLoginModalOpen(false);
            window.location.reload();
          }}
        />
      )}
      <MobileScreen>
        <Header
          className="topbar p-0 fixed w-full z-50"
          style={{ height: 76, lineHeight: "normal" }}
        >
          <div className="flex h-[76px] items-center gap-2 px-3.5">
            <NavLink className="flex min-w-0 flex-1 items-center gap-2" to="/">
              <div className="relative shrink-0">
                <div className="absolute inset-0 rounded-md bg-[#D4AF37]/25 blur-md" />
                <BrandLogo className="relative" size={36} />
              </div>
              <div className="min-w-0">
                <div className="brand-title text-[10px] font-semibold leading-[1.32] tracking-[0.035em] text-[#EFE5CE]">
                  Time Series Model<br />Research
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-[6.5px] uppercase tracking-[0.2em] text-[#7A6C50]">
                  <span className="status-pip" />
                  量化终端
                </div>
              </div>
            </NavLink>
            <nav aria-label="市场切换" className="flex shrink-0 items-center gap-1">
              <Select
                aria-label="选择市场"
                className="mobile-market-select"
                onChange={changeMarket}
                options={MARKET_TABS.map(({ value, label, flag }) => ({ value, label: `${flag} ${label}` }))}
                popupMatchSelectWidth={false}
                size="small"
                value={marketKey}
              />
            </nav>
          </div>
        </Header>
        <div className="h-screen overflow-hidden flex pt-[76px]">
          <div className="h-full overflow-hidden w-full">
            <div className="flex items-center flex-col w-full h-full overflow-auto">
              <MainContent />
            </div>
          </div>
        </div>
      </MobileScreen>
      <DesktopScreen>
        <Layout className="h-screen">
          <Sider className="app-sider" width={244}>
            <div className="flex h-full flex-col px-4 pb-5 pt-7">
              <NavLink
                className="flex flex-col items-center text-center select-none hover:cursor-pointer"
                to={"/"}
              >
                <div className="relative mb-4">
                  <div className="absolute inset-0 rounded-[18px] bg-[#D4AF37]/20 blur-2xl" />
                  <BrandLogo className="relative drop-shadow-[0_10px_25px_rgba(0,0,0,0.55)]" size={66} />
                </div>
                <div className="brand-title text-[13px] font-semibold leading-[1.4] tracking-[0.04em] text-[#EDE3CB]">
                  Time Series Model<br />Research
                </div>
                <div className="mt-3 flex items-center gap-2 text-[9px] uppercase tracking-[0.28em] text-[#8A7A56]">
                  <span className="status-dot" />
                  量化终端
                </div>
              </NavLink>

              <div className="mt-8 h-px w-full bg-gradient-to-r from-transparent via-[#D4AF37]/25 to-transparent" />

              <nav aria-label="市场导航" className="mt-6 w-full space-y-1.5">
                {MARKET_TABS.map((tab) => (
                  <NavLink
                    className={`nav-item ${marketKey === tab.value ? "active" : ""}`}
                    key={tab.value}
                    to={tab.path}
                  >
                    <span className="nav-flag">
                      {tab.value === "favor" ? (
                        <svg aria-hidden="true" fill="none" height="18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" viewBox="0 0 24 24" width="18">
                          <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.7-7.5 1.1-1.1a5.5 5.5 0 0 0 0-7.8Z" />
                        </svg>
                      ) : (
                        <span>{tab.flag}</span>
                      )}
                    </span>
                    <span>{tab.label}</span>
                  </NavLink>
                ))}
              </nav>

              {!hasStockServantToken && (
                <div className="mt-4 w-full border-t border-[#E8C37A]/12 pt-3">
                  <button
                    className="nav-item"
                    onClick={() => setIsLoginModalOpen(true)}
                    type="button"
                  >
                    <span className="nav-flag">
                      <svg aria-hidden="true" fill="none" height="17" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" viewBox="0 0 24 24" width="17">
                        <path d="M20 21a8 8 0 0 0-16 0" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </span>
                    <span>Sign in</span>
                  </button>
                </div>
              )}
            </div>
          </Sider>
          <Content className="bg-light-main flex">
            <MainContent />
          </Content>
        </Layout>
      </DesktopScreen>
    </div>
  );
}

const MainContent = () => {
  return (
    <StockScoreProvider>
      <Routes>
        {PageList.map((route) => {
          return (
            <Route
              exact
              path={route.path}
              key={route.path}
              element={route.component}
            ></Route>
          );
        })}
      </Routes>
    </StockScoreProvider>
  );
};

export default withRouter(Homepage);
